import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./CoursePageStyles.css";
import {
  FaArrowRight,
  FaBookOpen,
  FaClock,
  FaGraduationCap,
  FaLock,
  FaLockOpen,
  FaShieldAlt,
  FaSpinner,
} from "react-icons/fa";
import { sendPasswordResetEmail } from "firebase/auth";

import {
  courseDetailsMap,
  courseLevelLabels,
  courseLevelOrder,
  type CourseDetail,
  type CourseLevelKey,
} from "./CoursesDetails";

import Content from "./Content";
import Payment from "../../Authentication/Payment/Payment";
import { useAuth } from "../../Authentication/AuthContext";
import {
  clearBackendCourseAccessCache,
  getCourseAccessMap,
  isFullAccessEmail,
  type CourseAccessMap,
} from "../../Authentication/services/revenueCatWeb";
import { firebaseAuth } from "../../Authentication/services/firebase";
import CourseAuthSection, {
  type CourseAuthMode,
} from "./CourseAuthSection";
import CourseDetailSection from "./CourseDetailSection";

type CourseAccessStatus =
  | "idle"
  | "checking"
  | "active"
  | "inactive"
  | "error";

type GermanCourseDisplay = {
  title: string;
  label: string;
  duration: string;
  sessions: string;
  books: string;
};

type CachedCourseAccessMap = {
  saved_at: number;
  data: CourseAccessMap;
};

const COURSE_RESET_EVENT = "dear-language-course-reset";

const REMEMBER_LOGIN_KEY = "dear_language_course_remember_login";
const REMEMBER_EMAIL_KEY = "dear_language_course_remember_email";

const COURSE_ACCESS_CACHE_PREFIX = "dear-language-course-access-map-v4";
const OLD_COURSE_ACCESS_CACHE_PREFIXES = [
  "dear-language-course-access-map",
  "dear-language-course-access-map-v1",
  "dear-language-course-access-map-v2",
  "dear-language-course-access-map-v3",
];
const COURSE_ACCESS_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 12;

const GERMAN_COURSE_DISPLAY: Record<CourseLevelKey, GermanCourseDisplay> = {
  A1: {
    title: "A1-DEUTSCHKURS",
    label: "Anfänger",
    duration: "2 Monate",
    sessions: "2x pro Woche à 2 Stunden",
    books: "10 PDF-Bücher",
  },
  A2: {
    title: "A2-DEUTSCHKURS",
    label: "Grundstufe",
    duration: "2 Monate",
    sessions: "2x pro Woche à 2 Stunden",
    books: "10 PDF-Bücher",
  },
  B1: {
    title: "B1-DEUTSCHKURS",
    label: "Mittelstufe",
    duration: "3 Monate",
    sessions: "2x pro Woche à 2 Stunden",
    books: "10 PDF-Bücher",
  },
  B2: {
    title: "B2-DEUTSCHKURS",
    label: "Oberstufe",
    duration: "3 Monate",
    sessions: "2x pro Woche à 2 Stunden",
    books: "10 PDF-Bücher",
  },
};

const createEmptyCourseAccessMap = (): CourseAccessMap => ({
  full: false,
  A1: false,
  A2: false,
  B1: false,
  B2: false,
  C1: false,
  C2: false,
});

const createFullCourseAccessMap = (): CourseAccessMap => ({
  full: true,
  A1: true,
  A2: true,
  B1: true,
  B2: true,
  C1: true,
  C2: true,
});

const normalizeCourseAccessMap = (
  rawMap?: Partial<CourseAccessMap> | null,
): CourseAccessMap => {
  if (rawMap?.full) {
    return createFullCourseAccessMap();
  }

  return {
    full: false,
    A1: Boolean(rawMap?.A1),
    A2: Boolean(rawMap?.A2),
    B1: Boolean(rawMap?.B1),
    B2: Boolean(rawMap?.B2),
    C1: Boolean(rawMap?.C1),
    C2: Boolean(rawMap?.C2),
  };
};

const safeGetLocalStorageItem = (key: string): string | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return null;
    }

    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetLocalStorageItem = (key: string, value: string): boolean => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return false;
    }

    window.localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn("LocalStorage save skipped:", error);
    return false;
  }
};

const safeRemoveLocalStorageItem = (key: string): void => {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }

    window.localStorage.removeItem(key);
  } catch (error) {
    console.warn("LocalStorage remove skipped:", error);
  }
};

const getCourseAccessCacheKey = (email: string): string => {
  return `${COURSE_ACCESS_CACHE_PREFIX}:${email.trim().toLowerCase()}`;
};

const clearOldCourseAccessCaches = (email?: string | null): void => {
  const cleanEmail = email?.trim().toLowerCase() || "";

  if (!cleanEmail) {
    return;
  }

  try {
    OLD_COURSE_ACCESS_CACHE_PREFIXES.forEach((prefix) => {
      window.sessionStorage.removeItem(`${prefix}:${cleanEmail}`);
    });
  } catch {
    // Cache löschen ist optional.
  }
};

const getCachedCourseAccessMap = (email: string): CourseAccessMap | null => {
  try {
    const cleanEmail = email.trim().toLowerCase();
    const cacheKey = getCourseAccessCacheKey(cleanEmail);
    const cachedValue = window.sessionStorage.getItem(cacheKey);

    if (!cachedValue) {
      return null;
    }

    const parsed = JSON.parse(cachedValue) as CachedCourseAccessMap;

    if (!parsed?.data || !parsed?.saved_at) {
      window.sessionStorage.removeItem(cacheKey);
      return null;
    }

    if (Date.now() - parsed.saved_at > COURSE_ACCESS_CACHE_MAX_AGE_MS) {
      window.sessionStorage.removeItem(cacheKey);
      return null;
    }

    return normalizeCourseAccessMap(parsed.data);
  } catch {
    return null;
  }
};

const saveCourseAccessMapToCache = (
  email: string,
  accessMap: CourseAccessMap,
): void => {
  const cleanEmail = email.trim().toLowerCase();

  if (!cleanEmail) {
    return;
  }

  try {
    const cacheKey = getCourseAccessCacheKey(cleanEmail);

    const cachedValue: CachedCourseAccessMap = {
      saved_at: Date.now(),
      data: normalizeCourseAccessMap(accessMap),
    };

    window.sessionStorage.setItem(cacheKey, JSON.stringify(cachedValue));
  } catch {
    // Cache ist optional.
  }
};

const removeCourseAccessMapFromCache = (email?: string | null): void => {
  const cleanEmail = email?.trim().toLowerCase() || "";

  if (!cleanEmail) {
    return;
  }

  try {
    window.sessionStorage.removeItem(getCourseAccessCacheKey(cleanEmail));
    clearOldCourseAccessCaches(cleanEmail);
  } catch {
    // Cache löschen ist optional.
  }
};

const CoursePage: React.FC = () => {
  const {
    user,
    userId,
    userEmail,
    isLoggedIn,
    authLoading,
    authError,
    login,
    register,
    logout,
    clearAuthError,
  } = useAuth();

  const [selectedCourseKey, setSelectedCourseKey] =
    useState<CourseLevelKey | null>(null);

  const [authMode, setAuthMode] = useState<CourseAuthMode>("login");
  const [displayName, setDisplayName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [resetEmail, setResetEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [rememberLogin, setRememberLogin] = useState<boolean>(true);
  const [localLoginError, setLocalLoginError] = useState<string>("");
  const [resetPasswordMessage, setResetPasswordMessage] = useState<string>("");
  const [resetPasswordSending, setResetPasswordSending] =
    useState<boolean>(false);
  const [authSubmitting, setAuthSubmitting] = useState<boolean>(false);

  const [courseAccessStatus, setCourseAccessStatus] =
    useState<CourseAccessStatus>("idle");

  const [courseAccessError, setCourseAccessError] = useState<string>("");
  const [courseAccessMap, setCourseAccessMap] = useState<CourseAccessMap>(
    createEmptyCourseAccessMap,
  );
  const [courseAccessLoading, setCourseAccessLoading] =
    useState<boolean>(false);
  const [accessRefreshKey, setAccessRefreshKey] = useState<number>(0);

  const selectedCourse: CourseDetail | null = selectedCourseKey
    ? courseDetailsMap[selectedCourseKey]
    : null;

  const selectedCourseLabel = useMemo(() => {
    if (!selectedCourseKey) {
      return "";
    }

    return (
      GERMAN_COURSE_DISPLAY[selectedCourseKey]?.label ||
      courseLevelLabels[selectedCourseKey]
    );
  }, [selectedCourseKey]);

  const loggedInName =
    user?.displayName || userEmail?.split("@")[0] || "Lernender";

  const cleanUserEmail = userEmail?.trim().toLowerCase() || "";

  const hasFullAccess = Boolean(
    isLoggedIn && (courseAccessMap.full || isFullAccessEmail(userEmail)),
  );

  const selectedCourseAccessValue = selectedCourseKey
    ? courseAccessMap[selectedCourseKey]
    : false;

  const courseHasAccess = useCallback(
    (level: CourseLevelKey): boolean => {
      return Boolean(isLoggedIn && (hasFullAccess || courseAccessMap[level]));
    },
    [courseAccessMap, hasFullAccess, isLoggedIn],
  );

  const totalCourses = courseLevelOrder.length;

  const unlockedCourseCount = useMemo(() => {
    if (!isLoggedIn) {
      return 0;
    }

    if (hasFullAccess) {
      return totalCourses;
    }

    return courseLevelOrder.filter((level) => courseHasAccess(level)).length;
  }, [courseHasAccess, hasFullAccess, isLoggedIn, totalCourses]);

  const headerTitle = hasFullAccess
    ? "Alle Deutschkurse"
    : isLoggedIn
      ? "Meine Deutschkurse"
      : "Deutschkurse";

  const headerSubtitle = !isLoggedIn
    ? "Alle Kurse sind sichtbar. Zum Öffnen bitte einloggen oder Kurszugang kaufen."
    : hasFullAccess
      ? "Vollzugang: Alle Kurse sind freigeschaltet."
      : "Freigeschaltete Kurse können geöffnet werden. Gesperrte Kurse zeigen Login und Zahlung sicher im selben Bereich.";

  const isSelectedCourseUnlocked = Boolean(
    selectedCourseKey && courseHasAccess(selectedCourseKey),
  );

  const isContentOpen = Boolean(
    selectedCourse &&
      selectedCourseKey &&
      isLoggedIn &&
      isSelectedCourseUnlocked,
  );

  const isCheckingCourseAccess = Boolean(
    authLoading || (isLoggedIn && courseAccessLoading),
  );

  const resetCoursePage = useCallback((): void => {
    setSelectedCourseKey(null);
    setAuthMode("login");
    setDisplayName("");
    setPassword("");
    setLocalLoginError("");
    setResetPasswordMessage("");
    setCourseAccessError("");
    setCourseAccessStatus("idle");
    clearAuthError();

    if (!rememberLogin) {
      setEmail("");
      setResetEmail("");
    }
  }, [clearAuthError, rememberLogin]);

  useEffect(() => {
    document.documentElement.classList.add("course-screen-page");
    document.body.classList.add("course-screen-page");

    const savedRememberLogin =
      safeGetLocalStorageItem(REMEMBER_LOGIN_KEY) === "true";
    const savedEmail = safeGetLocalStorageItem(REMEMBER_EMAIL_KEY) ?? "";

    setRememberLogin(savedRememberLogin || Boolean(savedEmail));

    if (savedEmail) {
      setEmail(savedEmail);
      setResetEmail(savedEmail);
    }

    return () => {
      document.documentElement.classList.remove("course-screen-page");
      document.body.classList.remove("course-screen-page");
    };
  }, []);

  useEffect(() => {
    const handleCourseReset = (): void => {
      resetCoursePage();
      setAccessRefreshKey((current) => current + 1);
    };

    window.addEventListener(COURSE_RESET_EVENT, handleCourseReset);

    return () => {
      window.removeEventListener(COURSE_RESET_EVENT, handleCourseReset);
    };
  }, [resetCoursePage]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    let isCancelled = false;

    const loadAccessMap = async (): Promise<void> => {
      if (!isLoggedIn || !userId) {
        setCourseAccessMap(createEmptyCourseAccessMap());
        setCourseAccessStatus("idle");
        setCourseAccessError("");
        setCourseAccessLoading(false);
        return;
      }

      if (!cleanUserEmail) {
        setCourseAccessMap(createEmptyCourseAccessMap());
        setCourseAccessStatus("error");
        setCourseAccessError(
          "Ihre E-Mail-Adresse konnte nicht gelesen werden. Bitte loggen Sie sich erneut ein.",
        );
        setCourseAccessLoading(false);
        return;
      }

      clearOldCourseAccessCaches(cleanUserEmail);

      const cachedAccessMap = getCachedCourseAccessMap(cleanUserEmail);

      if (cachedAccessMap) {
        setCourseAccessMap(cachedAccessMap);
        setCourseAccessError("");
        setCourseAccessLoading(false);
        return;
      }

      setCourseAccessLoading(true);
      setCourseAccessError("");

      try {
        const nextAccessMap = await getCourseAccessMap(userId, cleanUserEmail);

        if (!isCancelled) {
          const normalizedMap = normalizeCourseAccessMap(nextAccessMap);

          setCourseAccessMap(normalizedMap);
          saveCourseAccessMapToCache(cleanUserEmail, normalizedMap);
        }
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Der Kurszugang konnte nicht geprüft werden.";

        if (!isCancelled) {
          setCourseAccessMap(createEmptyCourseAccessMap());
          setCourseAccessError(message);
        }
      } finally {
        if (!isCancelled) {
          setCourseAccessLoading(false);
        }
      }
    };

    void loadAccessMap();

    return () => {
      isCancelled = true;
    };
  }, [authLoading, isLoggedIn, userId, cleanUserEmail, accessRefreshKey]);

  useEffect(() => {
    setLocalLoginError("");
    setResetPasswordMessage("");
    setCourseAccessError("");
    setPassword("");
    clearAuthError();
  }, [selectedCourseKey, clearAuthError]);

  useEffect(() => {
    if (!selectedCourseKey || !isLoggedIn) {
      setCourseAccessStatus("idle");
      return;
    }

    if (courseAccessLoading) {
      setCourseAccessStatus("checking");
      return;
    }

    if (hasFullAccess || selectedCourseAccessValue) {
      setCourseAccessStatus("active");
      setCourseAccessError("");
      return;
    }

    setCourseAccessStatus("inactive");
  }, [
    selectedCourseKey,
    isLoggedIn,
    hasFullAccess,
    selectedCourseAccessValue,
    courseAccessLoading,
  ]);

  const handleCourseSelect = (level: CourseLevelKey): void => {
    if (isCheckingCourseAccess) {
      return;
    }

    const hasAccess = courseHasAccess(level);

    setSelectedCourseKey(level);
    setAuthMode("login");
    setLocalLoginError("");
    setResetPasswordMessage("");
    setCourseAccessError("");
    setCourseAccessStatus(
      hasAccess ? "active" : isLoggedIn ? "inactive" : "idle",
    );
  };

  const handleCourseCardKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    level: CourseLevelKey,
  ): void => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    handleCourseSelect(level);
  };

  const handleBackToCourses = (): void => {
    resetCoursePage();
  };

  const saveRememberLoginPreference = (cleanEmail: string): void => {
    if (rememberLogin) {
      safeSetLocalStorageItem(REMEMBER_LOGIN_KEY, "true");
      safeSetLocalStorageItem(REMEMBER_EMAIL_KEY, cleanEmail);
      return;
    }

    safeRemoveLocalStorageItem(REMEMBER_LOGIN_KEY);
    safeRemoveLocalStorageItem(REMEMBER_EMAIL_KEY);
  };

  const handleEmailChange = (value: string): void => {
    setEmail(value);

    if (authMode === "login") {
      setResetEmail(value);
    }
  };

  const handleOpenForgotPassword = (): void => {
    const cleanEmail = email.trim().toLowerCase();

    setAuthMode("forgot");
    setResetEmail(cleanEmail);
    setLocalLoginError("");
    setResetPasswordMessage("");
    clearAuthError();
  };

  const handleOpenLoginFromForgot = (): void => {
    setAuthMode("login");
    setLocalLoginError("");
    setResetPasswordMessage("");
    clearAuthError();

    const cleanResetEmail = resetEmail.trim().toLowerCase();

    if (cleanResetEmail) {
      setEmail(cleanResetEmail);
    }
  };

  const handleToggleAuthMode = (): void => {
    setAuthMode(authMode === "login" ? "register" : "login");
    setLocalLoginError("");
    setResetPasswordMessage("");
    clearAuthError();
  };

  const handleForgotPasswordSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();

    const cleanEmail = resetEmail.trim().toLowerCase();

    setLocalLoginError("");
    setResetPasswordMessage("");
    clearAuthError();

    if (!/\S+@\S+\.\S+/.test(cleanEmail)) {
      setLocalLoginError("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }

    setResetPasswordSending(true);

    try {
      await sendPasswordResetEmail(firebaseAuth, cleanEmail);

      setEmail(cleanEmail);

      if (rememberLogin) {
        safeSetLocalStorageItem(REMEMBER_LOGIN_KEY, "true");
        safeSetLocalStorageItem(REMEMBER_EMAIL_KEY, cleanEmail);
      }

      setResetPasswordMessage(
        "Wir haben Ihnen einen Link zum Zurücksetzen des Passworts per E-Mail gesendet.",
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Der Link zum Zurücksetzen konnte nicht gesendet werden.";

      setLocalLoginError(message);
    } finally {
      setResetPasswordSending(false);
    }
  };

  const handleAuthSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();

    const cleanEmail = email.trim().toLowerCase();
    const cleanPassword = password.trim();

    if (!/\S+@\S+\.\S+/.test(cleanEmail)) {
      setLocalLoginError("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }

    if (cleanPassword.length < 6) {
      setLocalLoginError("Das Passwort muss mindestens 6 Zeichen lang sein.");
      return;
    }

    setAuthSubmitting(true);
    setLocalLoginError("");
    setResetPasswordMessage("");
    clearAuthError();

    const success =
      authMode === "login"
        ? await login(cleanEmail, cleanPassword)
        : await register(cleanEmail, cleanPassword, displayName);

    setAuthSubmitting(false);

    if (success) {
      saveRememberLoginPreference(cleanEmail);
      setDisplayName("");
      setPassword("");
      setLocalLoginError("");
      setResetPasswordMessage("");
    }
  };

  const handleLogout = async (): Promise<void> => {
    await logout();
    setCourseAccessMap(createEmptyCourseAccessMap());
    setCourseAccessStatus("idle");
    setCourseAccessError("");
  };

  const handleRefreshCourseAccess = (): void => {
    removeCourseAccessMapFromCache(userEmail);
    clearBackendCourseAccessCache();
    setAccessRefreshKey((current) => current + 1);
  };

  const handleSubscribed = (): void => {
    setCourseAccessStatus("active");
    setCourseAccessError("");

    if (selectedCourseKey && cleanUserEmail) {
      setCourseAccessMap((current) => {
        const nextMap = normalizeCourseAccessMap({
          ...current,
          [selectedCourseKey]: true,
        });

        saveCourseAccessMapToCache(cleanUserEmail, nextMap);

        return nextMap;
      });
    } else {
      handleRefreshCourseAccess();
    }
  };

  const renderAuthSection = (loadingTitle?: string): React.ReactNode => {
    return (
      <CourseAuthSection
        authMode={authMode}
        loadingTitle={loadingTitle}
        displayName={displayName}
        email={email}
        resetEmail={resetEmail}
        password={password}
        rememberLogin={rememberLogin}
        authSubmitting={authSubmitting}
        resetPasswordSending={resetPasswordSending}
        errorText={localLoginError || authError || ""}
        infoText={resetPasswordMessage}
        onDisplayNameChange={setDisplayName}
        onEmailChange={handleEmailChange}
        onResetEmailChange={setResetEmail}
        onPasswordChange={setPassword}
        onRememberLoginChange={setRememberLogin}
        onAuthSubmit={handleAuthSubmit}
        onForgotPasswordSubmit={handleForgotPasswordSubmit}
        onBackToCourses={handleBackToCourses}
        onOpenForgotPassword={handleOpenForgotPassword}
        onOpenLoginFromForgot={handleOpenLoginFromForgot}
        onToggleAuthMode={handleToggleAuthMode}
        onLogout={handleLogout}
      />
    );
  };

  const renderPaymentSection = (): React.ReactNode => {
    if (!selectedCourse || !selectedCourseKey) {
      return null;
    }

    return (
      <aside className="course-login-column course-payment-column">
        <Payment
          courseLevel={selectedCourseKey}
          course={selectedCourse}
          userId={userId || ""}
          userEmail={userEmail}
          accessError={courseAccessStatus === "error" ? courseAccessError : ""}
          onSubscribed={handleSubscribed}
          onLogout={handleLogout}
          onBackToCourses={handleBackToCourses}
        />
      </aside>
    );
  };

  const renderCourseDetailItem = (
    label: string,
    value: string,
    icon: React.ReactNode,
  ): React.ReactNode => {
    return (
      <div className="course-detail-chip">
        <span className="course-detail-chip-icon">{icon}</span>
        <span className="course-detail-chip-label">{label}</span>
        <strong className="course-detail-chip-value">{value}</strong>
      </div>
    );
  };

  const renderStatusNotice = (): React.ReactNode => {
    if (!isLoggedIn || courseAccessLoading || !courseAccessError) {
      return null;
    }

    return (
      <article className="course-learning-card course-status-card">
        <div className="course-card-particles" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>

        <div className="course-card-badge course-card-badge-status">
          <section className="course-card-left">
            <div className="course-lock-block">
              <div className="course-level-icon">
                <FaShieldAlt />
              </div>

              <span className="course-lock-status">Zugang</span>
            </div>

            <div className="course-title-block">
              <h2>Prüfen</h2>
              <p>Bitte erneut versuchen</p>
            </div>
          </section>

          <section className="course-card-right">
            <div className="course-error-panel">
              <p>{courseAccessError}</p>
            </div>

            <button
              type="button"
              className="course-refresh-button"
              onClick={(event) => {
                event.stopPropagation();
                handleRefreshCourseAccess();
              }}
            >
              <span>Erneut prüfen</span>
              <FaArrowRight />
            </button>
          </section>
        </div>
      </article>
    );
  };

  return (
    <div
      className={`course-home-container ${
        isContentOpen ? "course-home-container-content-open" : ""
      }`}
    >
      <main
        className={`course-page ${
          isContentOpen ? "course-page-content-open" : ""
        }`}
      >
        {!selectedCourse && (
          <section className="course-level-section">
            <div className="course-screen-stack">
              <header className="course-header-card">
                <div className="course-header-content">
                  <span className="course-kicker">Deutsch Akademie</span>
                  <h1 className="course-page-title">{headerTitle}</h1>
                  <p className="course-subtitle">{headerSubtitle}</p>
                </div>

                <div className="course-header-badge">
                  <strong>
                    {courseAccessLoading && isLoggedIn
                      ? "..."
                      : `${unlockedCourseCount}/${totalCourses}`}
                  </strong>
                  <span>Freigeschaltete Kurse</span>
                </div>
              </header>

              <div className="course-learning-grid">
                {courseLevelOrder.map((level) => {
                  const germanCourse = GERMAN_COURSE_DISPLAY[level];
                  const isUnlocked = courseHasAccess(level);
                  const isChecking = isCheckingCourseAccess;
                  const cardStatusClassName = isChecking
                    ? "course-card-checking"
                    : isUnlocked
                      ? "course-card-unlocked"
                      : "course-card-locked";
                  const levelIconClassName = isChecking
                    ? "course-level-icon-checking"
                    : isUnlocked
                      ? "course-level-icon-unlocked"
                      : "course-level-icon-locked";
                  const statusText = isChecking
                    ? "Prüfen"
                    : isUnlocked
                      ? "Freigeschaltet"
                      : "Gesperrt";

                  return (
                    <button
                      key={level}
                      type="button"
                      className={`course-learning-card course-card-${level.toLowerCase()} ${cardStatusClassName}`}
                      onClick={() => handleCourseSelect(level)}
                      onKeyDown={(event) =>
                        handleCourseCardKeyDown(event, level)
                      }
                      aria-label={`${germanCourse.title} öffnen`}
                    >
                      <div className="course-card-particles" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                        <span />
                        <span />
                        <span />
                      </div>

                      <div className="course-card-badge">
                        <section className="course-card-left">
                          <div className="course-lock-block">
                            <div
                              className={`course-level-icon ${levelIconClassName}`}
                            >
                              {isChecking ? (
                                <FaSpinner className="course-checking-icon" />
                              ) : isUnlocked ? (
                                <FaLockOpen />
                              ) : (
                                <FaLock />
                              )}
                            </div>

                            <span className="course-lock-status">
                              {statusText}
                            </span>
                          </div>

                          <div className="course-title-block">
                            <h2>{level}</h2>
                            <p>{germanCourse.label}</p>
                          </div>
                        </section>

                        <section className="course-card-right">
                          <div className="course-detail-chip course-detail-chip-main">
                            <span className="course-detail-chip-icon">
                              <FaGraduationCap />
                            </span>

                            <span className="course-detail-chip-label">
                              Kurs
                            </span>

                            <strong className="course-detail-chip-value">
                              {germanCourse.title}
                            </strong>
                          </div>

                          {renderCourseDetailItem(
                            "Dauer",
                            germanCourse.duration,
                            <FaClock />,
                          )}

                          {renderCourseDetailItem(
                            "Unterricht",
                            germanCourse.sessions,
                            <FaGraduationCap />,
                          )}

                          {renderCourseDetailItem(
                            "Bücher",
                            germanCourse.books,
                            <FaBookOpen />,
                          )}
                        </section>
                      </div>
                    </button>
                  );
                })}

                {renderStatusNotice()}
              </div>
            </div>
          </section>
        )}

        {selectedCourse && selectedCourseKey && isLoggedIn && isContentOpen && (
          <section
            className={`course-detail-wrapper course-content-open course-${selectedCourseKey.toLowerCase()}`}
          >
            <Content
              courseLevel={selectedCourseKey}
              course={selectedCourse}
              studentName={loggedInName}
            />
          </section>
        )}

        {selectedCourse && selectedCourseKey && !isContentOpen && (
          <section
            className={`course-detail-wrapper course-login-open course-${selectedCourseKey.toLowerCase()}`}
          >
            <div className="course-login-layout">
              {authLoading && renderAuthSection("Anmeldung wird geprüft")}

              {!authLoading && !isLoggedIn && renderAuthSection()}

              {!authLoading &&
                isLoggedIn &&
                courseAccessStatus === "checking" &&
                renderAuthSection("Kurszugang wird geprüft")}

              {!authLoading &&
                isLoggedIn &&
                (courseAccessStatus === "inactive" ||
                  courseAccessStatus === "error") &&
                renderPaymentSection()}

              <CourseDetailSection
                courseLevel={selectedCourseKey}
                course={selectedCourse}
                courseLabel={selectedCourseLabel}
              />
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default CoursePage;